import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { LanguageContext } from './LanguageContext'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import { IDIOMA_DA_URL, irParaIdioma, idiomaDaSessao, guardarIdiomaDaSessao } from '../lib/idiomaUrl'
import { carregarCore, carregarArea, areaDaRota } from '../i18n/locales'

const IDIOMAS_SUPORTADOS = ['pt', 'en', 'es']

/** Idioma do aparelho (Isaias, 01/10/2026): português → pt, espanhol → es,
 *  qualquer outro (ou nenhum) → inglês. Vale a 1ª língua da lista do
 *  navegador que o portal fala. */
export function idiomaDoNavegador() {
  try {
    const lista = navigator.languages?.length ? navigator.languages : [navigator.language]
    for (const l of lista) {
      const base = String(l || '').slice(0, 2).toLowerCase()
      if (IDIOMAS_SUPORTADOS.includes(base)) return base
    }
  } catch { /* sem navigator */ }
  return 'en'
}

function getNested(obj, path) {
  // Converte "specializations[0]" → "specializations.0" para suportar arrays
  const normalized = path.replace(/\[(\d+)\]/g, '.$1')
  return normalized.split('.').reduce((acc, key) => {
    if (acc == null) return undefined
    // Tenta como índice numérico (para arrays)
    const idx = /^\d+$/.test(key) ? parseInt(key, 10) : key
    return acc[idx]
  }, obj)
}

function deepMerge(target, ...sources) {
  const result = { ...target }
  for (const source of sources) {
    for (const key of Object.keys(source)) {
      if (
        source[key] &&
        typeof source[key] === 'object' &&
        !Array.isArray(source[key])
      ) {
        result[key] = deepMerge(result[key] || {}, source[key])
      } else {
        result[key] = source[key]
      }
    }
  }
  return result
}

export function LanguageProvider({ children }) {
  const { pathname } = useLocation()
  const { user, perfil } = useAuth()

  // Padrão = idioma do APARELHO (pt/es/en; outra língua cai no inglês) —
  // não lê localStorage aqui de propósito. Idioma de visitante sem conta é só
  // desta sessão (troca manual no menu funciona, mas não sobrevive a uma
  // nova visita); só a CONTA guarda preferência de idioma de verdade,
  // aplicada pelo efeito abaixo assim que `perfil.locale` chega.
  const [locale, setLocale] = useState(() => IDIOMA_DA_URL || (IDIOMAS_SUPORTADOS.includes(idiomaDaSessao()) ? idiomaDaSessao() : idiomaDoNavegador()))
  // Depois que a conta já aplicou seu idioma salvo uma vez, uma troca
  // manual no menu não deve ser sobrescrita se `perfil` for recarregado
  // por outro motivo (ex.: outra aba atualiza o perfil) — só reage à
  // MUDANÇA de perfil.locale, não a toda renderização.
  const perfilLocaleAplicadoRef = useRef(null)

  // Núcleo do idioma ativo. Enquanto não chega, o app não renderiza —
  // é o que evita a página piscar com as chaves cruas no lugar do texto.
  const [core, setCore] = useState(null)

  // Áreas pesadas e traduções de jogo (Gangues) carregadas sob demanda,
  // indexadas por idioma pra não vazar texto de um idioma no outro.
  const [extras, setExtras] = useState({})

  useEffect(() => {
    let ativo = true
    carregarCore(locale).then(dados => { if (ativo) setCore(dados) })
    return () => { ativo = false }
  }, [locale])

  // `<html lang>` fixo em "pt-BR" (SEO/auditoria achou, 18/09/2026) nunca
  // acompanhava o idioma escolhido — visitante en/es via lang errado no
  // <html>, o que é sinal de acessibilidade (leitor de tela escolhe voz/
  // pronúncia pelo atributo) e um dos poucos sinais de idioma que o Google
  // ainda lê sem depender de hreflang/URL por idioma.
  useEffect(() => {
    document.documentElement.lang = locale === 'en' ? 'en' : locale === 'es' ? 'es' : 'pt-BR'
  }, [locale])

  // Mantém `ldi-locale` no localStorage como espelho do idioma ATIVO agora
  // — não é mais a fonte do padrão inicial (isso é o idioma do aparelho
  // pra quem não tem conta), só serve pra outros módulos fora do Context (splash
  // do index.html, Rádio Nina, Tamagoshi, TopTrumps) saberem em que
  // idioma a UI está nesta sessão.
  useEffect(() => {
    try { localStorage.setItem('ldi-locale', locale) } catch { /* ignora */ }
  }, [locale])

  // Conta logada: aplica o idioma salvo no perfil assim que ele chega
  // (login, ou perfil recém-criado no cadastro). Só reage quando o valor
  // de fato MUDA, pra uma troca manual no menu (que já atualizou `locale`
  // localmente) não ser desfeita se `perfil` for recarregado por outro
  // motivo depois.
  useEffect(() => {
    const salvo = perfil?.locale
    if (!salvo || !IDIOMAS_SUPORTADOS.includes(salvo)) return
    // endereço /pt ou /es diz o idioma desta visita: a conta não troca por cima
    if (IDIOMA_DA_URL) return
    if (perfilLocaleAplicadoRef.current === salvo) return
    perfilLocaleAplicadoRef.current = salvo
    setLocale(salvo)
  }, [perfil?.locale])

  // A rota diz qual área precisa existir. Carrega uma vez por idioma.
  useEffect(() => {
    const area = areaDaRota(pathname)
    if (!area) return
    const marca = `${area}:${locale}`
    if (extras[marca]) return
    let ativo = true
    carregarArea(area, locale).then(dados => {
      if (ativo && dados) setExtras(prev => ({ ...prev, [marca]: dados }))
    })
    return () => { ativo = false }
  }, [pathname, locale, extras])

  const dicionario = useMemo(() => {
    if (!core) return null
    const doIdioma = Object.entries(extras)
      .filter(([marca]) => marca.endsWith(`:${locale}`))
      .map(([, dados]) => dados)
    return doIdioma.length ? deepMerge(core, ...doIdioma) : core
  }, [core, extras, locale])

  const t = useCallback((path, vars) => {
    let result = dicionario ? getNested(dicionario, path) : undefined
    if (result == null) result = path
    if (vars && typeof result === 'string') {
      Object.entries(vars).forEach(([k, v]) => {
        result = result.replace(new RegExp(`\\{\\{?${k}\\}?\\}`, 'g'), v) // aceita {k} e {{k}}
      })
    }
    return result
  }, [dicionario])

  const tt = useCallback((path, vars) => {
    return t(`games.toptrumps.${path}`, vars)
  }, [t])

  const changeLocale = useCallback((next) => {
    // numa versão /pt ou /es: vai pro endereço da outra língua (grava a conta antes)
    guardarIdiomaDaSessao(next)
    const vaiNavegar = irParaIdioma(next)
    if (!vaiNavegar) setLocale(next)
    // Sem conta: a troca vale só pra esta sessão (nunca persiste) — é o
    // pedido central, primeiro contato sempre em inglês. Com conta: grava
    // no perfil, é isso que faz o idioma voltar sozinho no próximo login.
    if (user?.id) {
      perfilLocaleAplicadoRef.current = next
      supabase.from('profiles').update({ locale: next }).eq('id', user.id)
        .then(({ error }) => {
          if (error) console.error('[Idioma] falha ao salvar preferência da conta:', error)
        })
    }
  }, [user?.id])

  // Usado por quem carrega tradução própria (ex.: useGanguesI18n).
  const registerLocaleData = useCallback((localeKey, data) => {
    setExtras(prev => {
      const marca = `registrado:${localeKey}`
      return { ...prev, [marca]: deepMerge(prev[marca] || {}, data) }
    })
  }, [])

  const valor = useMemo(
    () => ({ locale, t, tt, changeLocale, registerLocaleData }),
    [locale, t, tt, changeLocale, registerLocaleData]
  )

  // A vinheta de abertura (index.html) cobre esta espera; ela só encerra
  // quando o App monta, e o App só monta com o dicionário na mão.
  if (!dicionario) return null

  return (
    <LanguageContext.Provider value={valor}>
      {children}
    </LanguageContext.Provider>
  )
}
